<template>
  <div class="study-effectiveness-mobile">
    <!-- 数据概览卡片 -->
    <div class="chart-card">
      <div class="card-header rounded-header">
        <span class="card-title">数据概览</span>
        <div class="card-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportCorrection" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenCorrection" />
        </div>
      </div>
      <div class="data-statistics">
        <div class="statistic-item">
          <div class="statistic-label">赛事</div>
          <div class="statistic-value">{{ chartData.correction.total_games }}</div>
        </div>
        <div class="statistic-item">
          <div class="statistic-label">题目</div>
          <div class="statistic-value">{{ chartData.correction.total }}</div>
        </div>
        <div class="statistic-item">
          <div class="statistic-label">计划</div>
          <div class="statistic-value">
            <span class="highlight">{{ chartData.plan.completed }}</span>
            <span class="separator">/</span>
            <span>{{ chartData.plan.total }}</span>
          </div>
        </div>
      </div>
      <div ref="correctionChart" class="chart-container"></div>
    </div>

    <!-- 全年赛事图卡片 -->
    <div class="chart-card">
      <div class="card-header rounded-header">
        <span class="card-title">全年赛事图</span>
        <div class="card-actions">
          <el-select
            v-model="selectedYear"
            size="mini"
            class="year-select"
            @change="handleYearChange"
          >
            <el-option
              v-for="year in yearOptions"
              :key="year"
              :label="year + '年'"
              :value="year"
            />
          </el-select>
          <el-button type="text" icon="el-icon-download" @click="handleExportPlan" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenPlan" />
        </div>
      </div>
      <div class="year-progress">
        <div class="progress-item">
          <span class="year">{{ selectedYear }}</span>
          <span class="progress">年进度</span>
          <span class="value">{{ yearProgress }}%</span>
        </div>
        <div class="progress-item">
          <span>你一共完成</span>
          <span class="value">{{ chartData.correction.total_games }}</span>
          <span>场赛事，今年完成</span>
          <span class="value">{{ chartData.plan.matrix.reduce((sum, item) => sum + item[2], 0) }}</span>
          <span>场赛事</span>
        </div>
      </div>
      <div ref="planChart" class="chart-container matrix-chart-container"></div>
    </div>

    <!-- 能力雷达图卡片 -->
    <div class="chart-card">
      <div class="card-header rounded-header">
        <span class="card-title">能力雷达</span>
        <div class="card-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportRadar" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenRadar" />
        </div>
      </div>
      <div ref="radarChart" class="chart-container radar-chart-container"></div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-loading-spinner></el-loading-spinner>
      <p>数据加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-if="error" class="error-container">
      <i class="el-icon-warning"></i>
      <p>{{ error }}</p>
      <el-button type="primary" size="small" @click="fetchData">重新加载</el-button>
    </div>

    <!-- 数据概览全屏弹窗 -->
    <el-dialog
      :visible.sync="correctionDialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleCorrectionDialogClosed"
      @open="handleCorrectionDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>数据概览</span>
        <div class="dialog-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportCorrectionFullscreen" />
          <el-button type="text" icon="el-icon-close" @click="closeCorrectionDialog" />
        </div>
      </div>
      <div class="fullscreen-chart" ref="correctionFullscreenChart"></div>
    </el-dialog>

    <!-- 全年赛事图全屏弹窗 -->
    <el-dialog
      :visible.sync="planDialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handlePlanDialogClosed"
      @open="handlePlanDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>全年赛事图</span>
        <div class="dialog-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportPlanFullscreen" />
          <el-button type="text" icon="el-icon-close" @click="closePlanDialog" />
        </div>
      </div>
      <div class="fullscreen-chart" ref="planFullscreenChart"></div>
    </el-dialog>

    <!-- 能力雷达全屏弹窗 -->
    <el-dialog
      :visible.sync="radarDialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleRadarDialogClosed"
      @open="handleRadarDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>能力雷达</span>
        <div class="dialog-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportRadarFullscreen" />
          <el-button type="text" icon="el-icon-close" @click="closeRadarDialog" />
        </div>
      </div>
      <div class="fullscreen-chart radar-chart" ref="radarFullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

export default {
  name: 'StudyEffectivenessMobile',
  data() {
    return {
      correctionChart: null,
      planChart: null,
      radarChart: null,
      loading: false,
      error: null,
      chartData: {
        correction: {
          rate: 0,
          total: 0,      // 总题目数
          total_games: 0,  // 赛事总数
          completed: 0,   // 已订正数
          remaining: 0,    // 未订正数
          corrected: 0,
          need_correction: 0,
          ac_questions: 0,
          pending_questions: 0
        },
        plan: {
          rate: 0,
          total: 0,      // 总计划数
          completed: 0,   // 已完成数
          in_progress: 0,  // 进行中数
          matrix: []      // 矩阵数据
        },
        ability: {
          all: [],
          recent: []
        }
      },
      selectedYear: new Date().getFullYear(),
      yearOptions: [],
      resizeHandler: null,
      // 弹窗相关数据
      correctionDialogVisible: false,
      planDialogVisible: false,
      radarDialogVisible: false,
      correctionFullscreenChart: null,
      planFullscreenChart: null,
      radarFullscreenChart: null,
      isInitializing: false
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    yearProgress() {
      const now = new Date()
      const currentYear = now.getFullYear()
      
      // 如果是历史年份，进度为100%
      if (this.selectedYear < currentYear) {
        return 100
      }
      
      // 如果是未来年份，进度为0%
      if (this.selectedYear > currentYear) {
        return 0
      }
      
      // 当前年份，计算实际进度
      const start = new Date(currentYear, 0, 1)
      const diff = now - start
      const oneYear = 1000 * 60 * 60 * 24 * 365
      return Math.min(Math.round((diff / oneYear * 100) * 100) / 100, 100)
    }
  },
  mounted() {
    this.initCharts()
    this.fetchData()
    this.initYearOptions()
    
    // 添加防抖的resize处理
    this.resizeHandler = _.debounce(() => {
      this.resizeCharts()
    }, 100)
    window.addEventListener('resize', this.resizeHandler)
  },
  beforeDestroy() {
    this.disposeCharts()
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    // 清理全屏图表
    if (this.correctionFullscreenChart) {
      this.correctionFullscreenChart.dispose()
    }
    if (this.planFullscreenChart) {
      this.planFullscreenChart.dispose()
    }
    if (this.radarFullscreenChart) {
      this.radarFullscreenChart.dispose()
    }
  },
  watch: {
    correctionDialogVisible(val) {
      if (!val && this.correctionFullscreenChart) {
        this.correctionFullscreenChart.dispose()
        this.correctionFullscreenChart = null
      }
    },
    planDialogVisible(val) {
      if (!val && this.planFullscreenChart) {
        this.planFullscreenChart.dispose()
        this.planFullscreenChart = null
      }
    },
    radarDialogVisible(val) {
      if (!val && this.radarFullscreenChart) {
        this.radarFullscreenChart.dispose()
        this.radarFullscreenChart = null
      }
    }
  },
  methods: {
    dataURLToBlob(dataURL) {
      const arr = dataURL.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)
      return new Blob([u8arr], { type: mime })
    },
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = filename
      link.href = url
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(url)
      document.body.removeChild(link)
    },
    initCharts() {
      this.correctionChart = echarts.init(this.$refs.correctionChart)
      this.planChart = echarts.init(this.$refs.planChart)
      this.radarChart = echarts.init(this.$refs.radarChart)
    },
    disposeCharts() {
      if (this.correctionChart) {
        this.correctionChart.dispose()
        this.correctionChart = null
      }
      if (this.planChart) {
        this.planChart.dispose()
        this.planChart = null
      }
      if (this.radarChart) {
        this.radarChart.dispose()
        this.radarChart = null
      }
    },
    resizeCharts() {
      this.correctionChart?.resize()
      this.planChart?.resize()
      this.radarChart?.resize()
    },
    initYearOptions() {
      const currentYear = new Date().getFullYear()
      this.yearOptions = Array.from(
        {length: currentYear - 2019}, 
        (_, i) => currentYear - i
      )
    },
    async fetchData() {
      try {
        this.loading = true
        this.error = null
        
        console.log('开始获取数据...')
        
        // 分别获取各个接口的数据
        let correctionData = {}, planData = {}, abilityData = {}
        
        try {
          console.log('请求订正数据...')
          const correctionRes = await request({
            url: '/api/analysis/question/correction',
            method: 'get'
          })
          console.log('订正数据响应:', correctionRes)
          correctionData = correctionRes.data || {}
          
          this.chartData.correction = {
            rate: correctionData.rate || 0,
            total: correctionData.total || 0,
            total_games: correctionData.total_games || 0,
            corrected: correctionData.corrected || 0,
            need_correction: correctionData.need_correction || 0,
            ac_questions: correctionData.ac_questions || 0,
            pending_questions: correctionData.pending_questions || 0
          }
        } catch (error) {
          console.error('获取订正数据失败:', error)
          throw new Error('获取订正数据失败')
        }

        try {
          console.log('请求计划数据...')
          const planCountRes = await request({
            url: '/api/analysis/plan/count',
            method: 'get'
          })
          console.log('计划数据响应:', planCountRes)
          const planCountData = planCountRes.data || {}
          const validTotalPlans = (planCountData.total || 0) - (planCountData.terminated || 0)
          
          this.chartData.plan = {
            ...this.chartData.plan,
            total: validTotalPlans,
            completed: planCountData.completed || 0,
            in_progress: planCountData.in_progress || 0,
            rate: (planCountData.completed / validTotalPlans * 100) || 0
          }
        } catch (error) {
          console.error('获取计划数据失败:', error)
          throw new Error('获取计划数据失败')
        }

        try {
          console.log('请求赛事矩阵数据...')
          const matrixRes = await request({
            url: '/api/analysis/game/matrix',
            method: 'get',
            params: {
              year: this.selectedYear
            }
          })
          console.log('赛事矩阵数据响应:', matrixRes)
          const matrixData = matrixRes.data || {}
          
          const matrix = []
          if (matrixData.matrix) {
            for (let month = 0; month < 12; month++) {
              for (let week = 0; week < 5; week++) {
                const value = matrixData.matrix[month][week] || 0
                matrix.push([month + 1, `W${week + 1}`, value])
              }
            }
          }
          console.log('处理后的矩阵数据:', matrix)
          
          this.chartData.plan.matrix = matrix
        } catch (error) {
          console.error('获取赛事矩阵数据失败:', error)
          throw new Error('获取赛事矩阵数据失败')
        }

        try {
          console.log('请求能力数据...')
          const abilityRes = await request({
            url: '/api/analysis/ability/radar',
            method: 'get'
          })
          console.log('能力数据响应:', abilityRes)
          abilityData = abilityRes.data || {}
          
          this.chartData.ability = {
            all: abilityData.all_indicators || [],
            recent: abilityData.recent_indicators || []
          }
        } catch (error) {
          console.error('获取能力数据失败:', error)
          throw new Error('获取能力数据失败')
        }

        console.log('更新图表...')
        this.updateCharts()
      } catch (error) {
        console.error('数据处理失败:', error)
        this.error = error.message
        this.$message.error('数据加载失败，请重试')
      } finally {
        this.loading = false
      }
    },
    updateCharts() {
      this.updateCorrectionChart()
      this.updatePlanChart()
      this.updateRadarChart()
    },
    updateCorrectionChart() {
      const correctionOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'item',
          confine: true,
          enterable: false,
          hideDelay: 100,
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 11
          },
          formatter: (params) => {
            const chartData = this.chartData
            if (params.seriesName === '计划-已完成' || params.seriesName === '计划-进行中') {
              const completedRate = (chartData.plan.completed / chartData.plan.total * 100).toFixed(1)
              const inProgressRate = (chartData.plan.in_progress / chartData.plan.total * 100).toFixed(1)
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">计划概览</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                    <div>已完成：${completedRate}%（${chartData.plan.completed}）</div>
                    <div>进行中：${inProgressRate}%（${chartData.plan.in_progress}）</div>
                    <div>总计划数：${chartData.plan.total}</div>
                  </div>
                </div>
              `
            }
            if (params.seriesName === 'AC部分' || params.seriesName === '订正率') {
              const acRate = (chartData.correction.ac_questions / chartData.correction.total * 100).toFixed(1)
              const correctionRate = chartData.correction.rate.toFixed(1)
              const needCorrectionRate = (chartData.correction.need_correction / chartData.correction.total * 100).toFixed(1)
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">数据概览</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  <div>AC率：${acRate}%</div>
                  <div>订正率：${correctionRate}%（需订正题目占比：${needCorrectionRate}%）</div>
                  <div>总题目数：${chartData.correction.total}</div>
                  <div>已订正：${chartData.correction.corrected}</div>
                  <div>需订正：${chartData.correction.need_correction}</div>
                  <div>已挂起：${chartData.correction.pending_questions}</div>
                </div>
              `
            }
            return ''
          }
        },
        legend: {
          show: true,
          bottom: 0,
          left: 'center',
          itemGap: 8,
          textStyle: {
            fontSize: 12,
            color: '#666666'
          },
          data: [
            {
              name: '订正率',
              itemStyle: { color: '#409EFF' }
            },
            {
              name: '计划-已完成',
              itemStyle: { color: '#E6A23C' }
            },
            {
              name: '计划-进行中',
              itemStyle: { color: '#F0C354' }
            }
          ]
        },
        grid: {
          top: '8%',
          bottom: '15%',
          left: '8%',
          right: '8%',
          containLabel: true
        },
        xAxis: {
          show: true,
          type: 'value',
          max: 100,
          min: 0,
          interval: 25,
          axisLabel: {
            show: true,
            formatter: '{value}%',
            color: '#999',
            fontSize: 10
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#E1E1E1'
            }
          },
          axisTick: { show: false },
          axisLine: { show: false }
        },
        yAxis: {
          type: 'category',
          data: ['计划完成率', '订正率'],
          axisLabel: { show: false },
          axisTick: { show: false },
          axisLine: { show: false }
        },
        series: [
          {
            name: '背景',
            type: 'bar',
            barWidth: 24,
            silent: true,
            itemStyle: {
              color: '#f5f7fa',
              borderRadius: 12
            },
            data: [100, 100]
          },
          {
            name: '计划-已完成',
            type: 'bar',
            barWidth: 24,
            stack: 'plan',
            barGap: '-100%',
            data: [{
              value: (this.chartData.plan.completed / this.chartData.plan.total * 100),
              itemStyle: {
                color: '#E6A23C',
                borderRadius: [12, 12, 12, 12]
              },
              label: {
                show: true,
                position: 'inside',
                fontSize: 12,
                color: '#ffffff',
                fontWeight: 'bold',
                formatter: (params) => params.value.toFixed(1) + '%'
              }
            }, null]
          },
          {
            name: '计划-进行中',
            type: 'bar',
            barWidth: 24,
            stack: 'plan',
            data: [{
              value: (this.chartData.plan.in_progress / this.chartData.plan.total * 100),
              itemStyle: {
                color: '#F0C354',
                borderRadius: [12, 12, 12, 12],
                decal: {
                  symbol: 'rect',
                  symbolSize: 3,
                  dashArrayX: [1, 0],
                  dashArrayY: [2, 4],
                  rotation: -0.785,
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              },
              label: {
                show: true,
                position: 'right',
                distance: 8,
                fontSize: 12,
                color: '#F0C354',
                fontWeight: 'bold',
                formatter: (params) => params.value.toFixed(1) + '%'
              }
            }, null]
          },
          {
            name: 'AC部分',
            type: 'bar',
            barWidth: 24,
            stack: 'correction',
            barGap: '-100%',
            data: [null, {
              value: (this.chartData.correction.ac_questions / this.chartData.correction.total * 100),
              itemStyle: {
                color: '#67C23A',
                borderRadius: [12, 12, 12, 12]
              },
              label: {
                show: true,
                position: 'inside',
                fontSize: 12,
                color: '#ffffff',
                fontWeight: 'bold',
                formatter: 'AC'
              }
            }]
          },
          {
            name: '订正率',
            type: 'bar',
            barWidth: 24,
            stack: 'correction',
            data: [null, {
              value: (this.chartData.correction.corrected / this.chartData.correction.total * 100),
              itemStyle: {
                color: '#409EFF',
                borderRadius: [12, 12, 12, 12],
                decal: {
                  symbol: 'rect',
                  symbolSize: 3,
                  dashArrayX: [1, 0],
                  dashArrayY: [2, 4],
                  rotation: -0.785,
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              },
              label: {
                show: true,
                position: 'right',
                distance: 8,
                fontSize: 12,
                color: '#409EFF',
                fontWeight: 'bold',
                formatter: () => this.chartData.correction.rate.toFixed(1) + '%'
              }
            }]
          }
        ]
      }

      this.correctionChart.setOption(correctionOption)
    },
    updatePlanChart() {
      const planOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          position: 'top',
          confine: true,
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 11
          },
          formatter: function(params) {
            if (params && params.data) {
              const count = params.data[2] || 0
              const dateLabel = params.data[3] || ''
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">${dateLabel}</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                    <div>赛事数量：${count} 场</div>
                  </div>
                </div>
              `
            }
            return ''
          }
        },
        grid: {
          top: '10%',
          bottom: '20%',
          left: '8%',
          right: '8%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
          axisLabel: {
            fontSize: 10,
            interval: 0
          },
          axisTick: { show: false },
          axisLine: { show: false }
        },
        yAxis: {
          type: 'category',
          data: ['第1周', '第2周', '第3周', '第4周', '第5周'],
          axisLabel: {
            fontSize: 10
          },
          axisTick: { show: false },
          axisLine: { show: false }
        },
        visualMap: {
          min: 0,
          max: 10,
          calculable: true,
          orient: 'horizontal',
          left: 'center',
          bottom: '2%',
          inRange: {
            color: ['#e0f3f8', '#abd9e9', '#74add1', '#4575b4']
          },
          textStyle: {
            fontSize: 10
          }
        },
        series: [{
          name: '赛事数量',
          type: 'heatmap',
          data: this.chartData.plan.matrix.map(item => {
            const weekIndex = parseInt(item[1].replace('W', '')) - 1
            return [item[0] - 1, weekIndex, item[2]]
          }),
          label: {
            show: true,
            color: '#fff',
            fontSize: 10,
            formatter: function(params) {
              return params.value[2] || '';
            }
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1
          }
        }]
      }

      this.planChart.setOption(planOption)
    },
    updateRadarChart() {
      const radarOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          confine: true,
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 11
          },
          formatter: (params) => {
            if (!params || !Array.isArray(params) || params.length === 0) return ''
            const seriesName = params[0].seriesName || ''
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${seriesName}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${params.map(param => {
                    const value = param.value || 0
                    const name = param.seriesName || ''
                    return `<div>${name}：${value.toFixed(1)}%</div>`
                  }).join('')}
                </div>
              </div>
            `
          }
        },
        legend: {
          data: ['全部赛事', '最近10场'],
          bottom: -5,
          left: 'center',
          orient: 'horizontal',
          itemGap: 20,
          textStyle: {
            fontSize: 12,
            color: '#666'
          },
          itemWidth: 12,
          itemHeight: 8
        },
        grid: {
          containLabel: true
        },
        radar: {
          indicator: this.chartData.ability.all.map((item, index) => {
            const recentValue = this.chartData.ability.recent[index]?.value || 0
            if (item.name === '排位') {
              return {
                name: `${item.name}\n{blue|${(100 - item.value).toFixed(1)}%}\n{green|${(100 - recentValue).toFixed(1)}%}`,
                max: 100
              }
            }
            return {
              name: `${item.name}\n{blue|${item.value}%}\n{green|${recentValue}%}`,
              max: 100
            }
          }) || [
            { name: '得分\n{blue|0%}\n{green|0%}', max: 100 },
            { name: '排位\n{blue|0%}\n{green|0%}', max: 100 },
            { name: 'AC\n{blue|0%}\n{green|0%}', max: 100 },
            { name: '订正\n{blue|0%}\n{green|0%}', max: 100 },
            { name: '掌握\n{blue|0%}\n{green|0%}', max: 100 },
            { name: '复盘\n{blue|0%}\n{green|0%}', max: 100 }
          ],
          radius: '60%',
          center: ['50%', '50%'],
          splitNumber: 4,
          axisName: {
            color: '#666',
            fontSize: 10,
            lineHeight: 12,
            padding: [2, 0, 0, 0],
            rich: {
              blue: {
                color: '#409EFF',
                fontSize: 10,
                padding: [0, 0, 0, 0]
              },
              green: {
                color: '#67C23A',
                fontSize: 10,
                padding: [0, 0, 0, 0]
              }
            }
          },
          splitLine: {
            lineStyle: {
              color: '#E1E1E1'
            }
          },
          splitArea: {
            show: true,
            areaStyle: {
              color: ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.1)']
            }
          }
        },
        series: [{
          name: '全部赛事',
          type: 'radar',
          z: 2,
          data: [{
            value: this.chartData.ability.all.map(item => item.value) || [0, 0, 0, 0, 0, 0],
            name: '全部赛事',
            areaStyle: {
              color: 'rgba(64, 158, 255, 0.2)'
            },
            lineStyle: {
              color: '#409EFF',
              width: 2
            },
            itemStyle: {
              color: '#409EFF'
            },
            symbolSize: 6
          }]
        }, {
          name: '最近10场',
          type: 'radar',
          z: 1,
          data: [{
            value: this.chartData.ability.recent.map(item => item.value) || [0, 0, 0, 0, 0, 0],
            name: '最近10场',
            areaStyle: {
              color: 'rgba(103, 194, 58, 0.2)'
            },
            lineStyle: {
              color: '#67C23A',
              width: 2
            },
            itemStyle: {
              color: '#67C23A'
            },
            symbolSize: 6
          }]
        }]
      }

      this.radarChart.setOption(radarOption)
    },
    handleYearChange() {
      this.fetchData()
    },
    // 数据概览相关方法
    handleExportCorrection() {
      const dpr = window.devicePixelRatio || 1
      const pixelRatio = Math.min(3, dpr * 1.5)
      const dataURL = this.correctionChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
      const blob = this.dataURLToBlob(dataURL)
      this.downloadBlob(blob, 'overview.png')
    },
    handleFullscreenCorrection() {
      this.correctionDialogVisible = true
    },
    handleCorrectionDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.correctionFullscreenChart
          if (!chartDom) return

          this.correctionFullscreenChart = echarts.init(chartDom)
          const option = this.correctionChart.getOption()
          
          // 全屏时调整配置
          option.grid = {
            left: '5%',
            right: '5%',
            bottom: '20%',
            top: '5%',
            containLabel: true
          }
          
          // 调整图例位置
          if (option.legend) {
            option.legend.bottom = '1%'
            option.legend.itemGap = 6
            option.legend.textStyle = {
              fontSize: 10
            }
          }

          this.correctionFullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.correctionFullscreenChart) {
              this.correctionFullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleCorrectionDialogClosed() {
      this.isInitializing = false
    },
    closeCorrectionDialog() {
      this.correctionDialogVisible = false
    },
    handleExportCorrectionFullscreen() {
      if (!this.correctionFullscreenChart) return
      
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.correctionFullscreenChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'overview.png')
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    // 全年赛事图相关方法
    handleExportPlan() {
      const dpr = window.devicePixelRatio || 1
      const pixelRatio = Math.min(3, dpr * 1.5)
      const dataURL = this.planChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
      const blob = this.dataURLToBlob(dataURL)
      this.downloadBlob(blob, 'year_overview.png')
    },
    handleFullscreenPlan() {
      this.planDialogVisible = true
    },
    handlePlanDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.planFullscreenChart
          if (!chartDom) return

          this.planFullscreenChart = echarts.init(chartDom)
          const option = this.planChart.getOption()
          
          // 全屏时调整配置
          option.grid = {
            left: '5%',
            right: '5%',
            bottom: '20%',
            top: '5%',
            containLabel: true
          }
          
          // 调整visualMap位置
          if (option.visualMap) {
            option.visualMap.bottom = '1%'
            option.visualMap.textStyle = {
              fontSize: 10
            }
          }

          this.planFullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.planFullscreenChart) {
              this.planFullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handlePlanDialogClosed() {
      this.isInitializing = false
    },
    closePlanDialog() {
      this.planDialogVisible = false
    },
    handleExportPlanFullscreen() {
      if (!this.planFullscreenChart) return
      
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.planFullscreenChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'year_overview.png')
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    // 能力雷达相关方法
    handleExportRadar() {
      const dpr = window.devicePixelRatio || 1
      const pixelRatio = Math.min(3, dpr * 1.5)
      const dataURL = this.radarChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
      const blob = this.dataURLToBlob(dataURL)
      this.downloadBlob(blob, 'ability_radar.png')
    },
    handleFullscreenRadar() {
      this.radarDialogVisible = true
    },
    handleRadarDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.radarFullscreenChart
          if (!chartDom) return

          this.radarFullscreenChart = echarts.init(chartDom)
          const option = this.radarChart.getOption()
          
          // 全屏时调整配置
          option.grid = {
            left: '5%',
            right: '5%',
            bottom: '15%',
            top: '8%',
            containLabel: true
          }
          
          // 调整图例位置
          if (option.legend) {
            option.legend.bottom = '2%'
          }

          this.radarFullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.radarFullscreenChart) {
              this.radarFullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleRadarDialogClosed() {
      this.isInitializing = false
    },
    closeRadarDialog() {
      this.radarDialogVisible = false
    },
    handleExportRadarFullscreen() {
      if (!this.radarFullscreenChart) return
      
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.radarFullscreenChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'ability_radar.png')
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    }
  }
}
</script>

<style scoped>
.study-effectiveness-mobile {
  padding: 0;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.chart-card {
  margin-bottom: 16px;
  background: #fff;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rounded-header {
  border-radius: 8px 8px 0 0;
}

.card-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.card-actions .el-button {
  color: #ffffff;
  font-size: 16px;
  padding: 4px;
  margin-left: 8px;
}

.card-actions .el-button:hover {
  color: #409EFF;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.year-select {
  width: 100px;
}

.year-select :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding-right: 25px;
  font-size: 12px;
}

.year-select :deep(.el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.year-select :deep(.el-input__suffix) {
  right: 5px;
}

.year-select :deep(.el-select__caret) {
  color: #fff;
  line-height: 28px;
}

.year-select :deep(.el-select__caret.is-reverse) {
  transform: rotateZ(180deg);
}

.data-statistics {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  gap: 12px;
}

.statistic-item {
  flex: 1;
  padding: 12px 16px;
  background: #F8F9FC;
  border-radius: 8px;
  text-align: center;
  transition: all 0.3s ease;
}

.statistic-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.statistic-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.statistic-value {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  line-height: 1.2;
}

.statistic-value .highlight {
  color: #409EFF;
}

.statistic-value .separator {
  margin: 0 4px;
  font-weight: normal;
  color: #999;
  font-size: 16px;
}

.year-progress {
  height: 100px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  background: #f8f9fa;
  box-sizing: border-box;
}

.progress-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 14px;
  color: #666;
}

.progress-item .year {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
}

.progress-item .value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
}

.chart-container {
  height: 300px;
  width: 100%;
  padding: 0 0 20px 0;
  box-sizing: border-box;
}

.matrix-chart-container {
  height: 350px;
}

.radar-chart-container {
  height: 400px;
  padding: 0 0 20px 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 16px;
}

.loading-container p {
  margin-top: 16px;
  color: #666;
  font-size: 14px;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
}

.error-container i {
  font-size: 48px;
  color: #f56c6c;
  margin-bottom: 16px;
}

.error-container p {
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .study-effectiveness-mobile {
    padding: 0;
  }
  
  .chart-card {
    margin-bottom: 12px;
    border-radius: 0;
  }
  
  .card-header {
    padding: 12px 16px;
  }
  
  .header-left {
    gap: 8px;
  }
  
  .rounded-header {
    border-radius: 8px 8px 0 0;
  }
  
  .card-actions .el-button {
    font-size: 14px;
    padding: 2px;
    margin-left: 4px;
  }
  
  .card-title {
    font-size: 14px;
  }
  
  .data-statistics {
    padding: 12px 16px;
    gap: 8px;
  }
  
  .statistic-item {
    padding: 8px 12px;
  }
  
  .statistic-label {
    font-size: 11px;
  }
  
  .statistic-value {
    font-size: 18px;
  }
  
  .chart-container {
    height: 280px;
    padding: 0 0 16px 0;
  }
  
  .matrix-chart-container {
    height: 320px;
  }
  
  .radar-chart-container {
    height: 360px;
    padding: 0 0 16px 0;
  }
  
  .year-progress {
    height: 80px;
    padding: 12px 16px;
  }
  
  .progress-item {
    font-size: 12px;
  }
  
  .progress-item .year {
    font-size: 20px;
  }
  
  .progress-item .value {
    font-size: 20px;
  }
  
  .fullscreen-chart {
    height: 300px;
    padding: 0;
  }
  
  .fullscreen-chart.radar-chart {
    height: 400px;
  }
}

/* 弹窗样式 */
.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-actions {
  display: flex;
  gap: 8px;
}

.dialog-actions .el-button {
  color: #fff;
  padding: 4px;
}

.dialog-actions .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 300px;
  padding: 0;
  box-sizing: border-box;
}

.fullscreen-chart.radar-chart {
  height: 400px;
}
</style> 